import { useEffect, useState, useRef } from "react";
import { Heart, X } from "lucide-react";
import { api } from "@/lib/api";
import { speak } from "@/lib/tts";

/**
 * Banner shown on the child's board when an undismissed family note exists.
 *
 * Design (locked by founder request):
 *   - One-way only: child cannot reply
 *   - No browser push, no system notifications — visible only in-app
 *   - Tap-to-dismiss; once dismissed, the banner is gone
 *   - Reads the message aloud (TTS) on tap so a non-reader child still gets it
 *
 * Polls every 30s while the board is open so the child sees a note within a
 * minute even if it arrives mid-session.
 */
export default function FamilyNoteBanner({ profileId }) {
  const [notes, setNotes] = useState([]);
  const [opened, setOpened] = useState(null); // currently expanded note
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchActive = async () => {
    if (!profileId) return;
    try {
      const { data } = await api.get(`/profiles/${profileId}/notes/active`);
      setNotes(data?.items || []);
    } catch (e) {
      // Silent — banner is non-critical; we don't want a transient API
      // error to shove a red toast in front of a child mid-class.
      console.error("[FamilyNoteBanner] fetch failed", e);
    }
  };

  useEffect(() => {
    fetchActive();
    pollRef.current = setInterval(fetchActive, 30000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  if (!notes.length) return null;

  const top = notes[0];

  const openNote = (note) => {
    setOpened(note);
    // Speak the message in the child's preferred voice/locale.
    // We fall back to default browser TTS if the family hasn't recorded
    // a custom parent voice yet (v1 — recorded voice notes are a v2 feature).
    speak(`${note.sender_name} says: ${note.message}`, { rate: 0.95 });
  };

  const dismiss = async () => {
    if (!opened) return;
    setBusy(true);
    try {
      await api.post(`/notes/${opened.id}/dismiss`);
      setNotes((prev) => prev.filter((n) => n.id !== opened.id));
      setOpened(null);
    } catch (e) {
      console.error("[FamilyNoteBanner] dismiss failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Sticky banner — pulsing heart, just above the board */}
      <button
        type="button"
        onClick={() => openNote(top)}
        className="w-full mb-3 brutal-card bg-gradient-to-r from-[color:var(--coral)] to-[#FF8E72] text-white p-3 flex items-center gap-3 hover:scale-[1.01] transition-transform"
        data-testid="family-note-banner"
        aria-label={`A note from ${top.sender_name}. Tap to read.`}
      >
        <span className="relative inline-flex shrink-0">
          <Heart size={28} fill="currentColor" className="text-white" />
          <span className="absolute inset-0 inline-flex animate-ping opacity-50">
            <Heart size={28} fill="currentColor" className="text-white" />
          </span>
        </span>
        <div className="text-left flex-1 min-w-0">
          <div className="display text-xs uppercase tracking-[0.25em] font-bold opacity-90">
            A note from {top.sender_name}
          </div>
          <div className="display text-base font-extrabold truncate">Tap to read 💛</div>
        </div>
        {notes.length > 1 && (
          <span
            className="bg-white text-[color:var(--coral-deep)] border-[2px] border-[color:var(--ink)] rounded-full px-2.5 py-0.5 text-xs font-extrabold display shrink-0"
            data-testid="family-note-banner-count"
          >
            +{notes.length - 1} more
          </span>
        )}
      </button>

      {/* Expanded card — large readable text + dismiss */}
      {opened && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="family-note-modal"
        >
          <div className="brutal-card bg-[color:var(--cream-deep)] max-w-md w-full p-6 relative">
            <button
              type="button"
              onClick={dismiss}
              disabled={busy}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-[2px] border-[color:var(--ink)] flex items-center justify-center hover:bg-[color:var(--paper-muted)]"
              data-testid="family-note-modal-close"
            >
              <X size={16} />
            </button>
            <div className="flex items-center gap-2 mb-4">
              <Heart size={22} fill="currentColor" className="text-[color:var(--coral)]" />
              <span className="display text-xs uppercase tracking-[0.25em] font-bold text-[color:var(--coral-deep)]">
                From {opened.sender_name}
              </span>
            </div>
            <p
              className="display text-3xl sm:text-4xl font-extrabold leading-snug mb-6"
              data-testid="family-note-message"
            >
              {opened.message}
            </p>
            <button
              type="button"
              onClick={dismiss}
              disabled={busy}
              className="pill-btn primary w-full text-lg py-3"
              data-testid="family-note-dismiss"
            >
              {busy ? "..." : "Got it 💛"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
